import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, API_BASE } from "../lib/api";
import { toast } from "sonner";
import { Users, ArrowLeft, Download, ChevronRight, Lightbulb, Inbox } from "lucide-react";
import { TicketCard } from "../components/TicketCard";
import InspirationBoard from "./InspirationBoard";

export default function WorkOrders() {
  const navigate = useNavigate();
  const { clientId } = useParams();
  const [clients, setClients] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [tab, setTab] = useState("active");
  const [view, setView] = useState("work-orders"); // "work-orders" | "inspiration"
  const [showReport, setShowReport] = useState(false);

  const load = async () => {
    const [c, t] = await Promise.all([api.get("/clients"), api.get("/tickets")]);
    setClients(c.data);
    setTickets(t.data);
    // Mark seen
    try { await api.post("/tickets/mark-seen"); } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const currentClient = useMemo(() => clients.find((c) => c.user_id === clientId), [clients, clientId]);

  const clientTickets = useMemo(() => {
    if (!clientId) return [];
    return tickets.filter((t) => t.client_id === clientId && t.status === tab);
  }, [tickets, clientId, tab]);

  if (!clientId) {
    return (
      <div data-testid="work-orders-page">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Admin</div>
            <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900">Clients</h1>
            <p className="mt-2 text-sm text-zinc-600">Select a client to view their work orders or inspiration board.</p>
          </div>
          <button data-testid="work-orders-report-button" onClick={() => setShowReport(true)} className="inline-flex items-center gap-2 rounded-md border border-zinc-200 bg-white px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50">
            <Download size={14} /> Monthly Report
          </button>
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {clients.map((c) => (
            <button
              key={c.user_id}
              data-testid={`work-orders-client-${c.user_id}`}
              onClick={() => navigate(`/work-orders/${c.user_id}`)}
              className="group flex items-center gap-4 rounded-xl border border-zinc-200 bg-white p-5 text-left transition-all hover:-translate-y-[1px] hover:border-[#0066ff] hover:shadow-md"
            >
              {c.logo_file_id ? (
                <img src={`${API_BASE}/files/${c.logo_file_id}`} alt="" className="h-12 w-12 rounded-lg object-cover" />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#E5F0FF] text-base font-semibold text-[#0066ff]">
                  {(c.name || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-zinc-900">{c.name}</div>
                <div className="truncate text-xs text-zinc-500">{c.email}</div>
                <div className="mt-1 flex items-center gap-2 text-[10px] text-zinc-500">
                  <span className="rounded-full bg-[#E5F0FF] px-1.5 py-0.5 font-semibold text-[#0066ff]">{c.tickets_active} active</span>
                  <span className="rounded-full bg-zinc-100 px-1.5 py-0.5">{c.tickets_complete} complete</span>
                </div>
              </div>
              <ChevronRight size={16} className="text-zinc-300 group-hover:text-[#0066ff]" />
            </button>
          ))}
          {clients.length === 0 && (
            <div className="col-span-full rounded-xl border border-dashed border-zinc-200 bg-white p-12 text-center">
              <Users size={28} className="mx-auto text-zinc-300" />
              <p className="mt-3 text-sm text-zinc-500">No clients yet. Add one in Settings.</p>
              <button onClick={() => navigate("/settings")} className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-[#0066ff] px-3 py-2 text-sm font-medium text-white hover:bg-[#0052cc]">
                Go to Settings
              </button>
            </div>
          )}
        </div>

        {showReport && <MonthlyReportModal onClose={() => setShowReport(false)} />}
      </div>
    );
  }

  return (
    <div data-testid="work-orders-client-page">
      <button onClick={() => navigate("/work-orders")} className="mb-6 inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900">
        <ArrowLeft size={14} /> All clients
      </button>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-4">
          {currentClient?.logo_file_id ? (
            <img src={`${API_BASE}/files/${currentClient.logo_file_id}`} alt="" className="h-14 w-14 rounded-lg object-cover" />
          ) : (
            <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-[#E5F0FF] text-lg font-semibold text-[#0066ff]">
              {(currentClient?.name || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Client</div>
            <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900">{currentClient?.name || "—"}</h1>
            <div className="mt-1 text-xs text-zinc-500">{currentClient?.email}</div>
          </div>
        </div>
      </div>

      {/* Top-level view toggle: Work Orders | Inspiration Board */}
      <div className="mt-6 flex items-center gap-1 rounded-lg border border-zinc-200 bg-white p-1 w-fit">
        {[
          { id: "work-orders", label: "Work Orders", icon: Inbox },
          { id: "inspiration", label: "Inspiration Board", icon: Lightbulb },
        ].map((v) => (
          <button
            key={v.id}
            data-testid={`work-orders-view-${v.id}`}
            onClick={() => setView(v.id)}
            className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              view === v.id ? "bg-[#E5F0FF] text-[#0066ff]" : "text-zinc-600 hover:bg-zinc-50"
            }`}
          >
            <v.icon size={13} /> {v.label}
          </button>
        ))}
      </div>

      {view === "inspiration" ? (
        <div className="mt-6">
          <InspirationBoard clientId={clientId} embedded />
        </div>
      ) : (
        <>
          <div className="mt-6 flex items-center gap-1 rounded-lg border border-zinc-200 bg-white p-1 w-fit">
            {[
              { id: "active", label: "Active", count: tickets.filter((t) => t.client_id === clientId && t.status === "active").length },
              { id: "complete", label: "Completed", count: tickets.filter((t) => t.client_id === clientId && t.status === "complete").length },
            ].map((t) => (
              <button
                key={t.id}
                data-testid={`work-orders-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                  tab === t.id ? "bg-[#E5F0FF] text-[#0066ff]" : "text-zinc-600 hover:bg-zinc-50"
                }`}
              >
                {t.label}
                <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${tab === t.id ? "bg-white text-[#0066ff]" : "bg-zinc-100 text-zinc-500"}`}>
                  {t.count}
                </span>
              </button>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {clientTickets.map((t) => (
              <TicketCard key={t.id} ticket={t} onClick={() => navigate(`/tickets/${t.id}`)} />
            ))}
            {clientTickets.length === 0 && (
              <div className="col-span-full rounded-xl border border-dashed border-zinc-200 bg-white p-10 text-center text-sm text-zinc-500">
                No tickets in this list.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function MonthlyReportModal({ onClose }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tickets/reports/monthly?year=${year}&month=${month}`);
      setReport(data);
    } catch {
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReport(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [year, month]);

  const downloadCsv = () => {
    if (!report) return;
    const header = ["ticket_id", "title", "client_name", "client_email", "category", "priority", "completed_at", "price_amount"];
    const lines = [header.join(",")];
    report.rows.forEach((r) => {
      lines.push(header.map((h) => {
        const v = r[h] ?? "";
        const s = String(v).replace(/"/g, '""');
        return /[",\n]/.test(s) ? `"${s}"` : s;
      }).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `work-orders-${year}-${String(month).padStart(2, "0")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div data-testid="monthly-report-modal" className="max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-8 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Reports</div>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900">Monthly Work Orders</h2>
          </div>
          <button onClick={onClose} className="rounded p-1 text-zinc-400 hover:bg-zinc-100">✕</button>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <select data-testid="report-month" value={month} onChange={(e) => setMonth(parseInt(e.target.value))} className="rounded-md border border-zinc-200 px-3 py-2 text-sm">
            {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m, i) => (
              <option key={m} value={i + 1}>{m}</option>
            ))}
          </select>
          <select data-testid="report-year" value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="rounded-md border border-zinc-200 px-3 py-2 text-sm">
            {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <button data-testid="report-download" onClick={downloadCsv} disabled={!report?.count} className="ml-auto inline-flex items-center gap-2 rounded-md bg-[#0066ff] px-3 py-2 text-sm font-medium text-white hover:bg-[#0052cc] disabled:opacity-50">
            <Download size={14} /> Download CSV
          </button>
        </div>

        <div className="mt-6">
          {loading ? (
            <div className="text-sm text-zinc-500">Loading…</div>
          ) : !report || report.count === 0 ? (
            <div className="rounded-md border border-dashed border-zinc-200 p-10 text-center text-sm text-zinc-500">No completed work orders this month.</div>
          ) : (
            <>
              <div className="mb-4 flex items-center justify-between rounded-lg bg-[#E5F0FF] p-4 text-sm">
                <div>Completed: <strong>{report.count}</strong></div>
                <div>Total: <strong>${report.total.toFixed(2)}</strong></div>
              </div>
              <div className="overflow-hidden rounded-lg border border-zinc-200">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 text-xs text-zinc-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">Title</th>
                      <th className="px-3 py-2 text-left font-medium">Client</th>
                      <th className="px-3 py-2 text-left font-medium">Completed</th>
                      <th className="px-3 py-2 text-right font-medium">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.rows.map((r) => (
                      <tr key={r.ticket_id} className="border-t border-zinc-100">
                        <td className="px-3 py-2 text-zinc-900">{r.title}</td>
                        <td className="px-3 py-2 text-zinc-700">{r.client_name}</td>
                        <td className="px-3 py-2 text-zinc-500">{r.completed_at ? new Date(r.completed_at).toLocaleDateString() : "—"}</td>
                        <td className="px-3 py-2 text-right text-zinc-900">
                          {typeof r.price_amount === "number" ? (r.price_amount === 0 ? "No cost" : `$${r.price_amount.toFixed(2)}`) : "—"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
